import { useCallback } from "react";
import {
  getHeadersWithUniqueVisitorId,
  SectionBasedProduct,
  streamApi,
} from "@brandclub/common-ui";
import { ConversationMessage } from "../types/conversation";
import {
  extractStreamingProductGroups,
  processProductGroupTags,
} from "../utils/productGroupParser";

interface UseStreamingSearchProps {
  brandId: string | null;
  appConfigId: string | null;
  conversationId: string | null;
  setConversationId: (id: string) => void;
  setIsActivelyStreaming: (streaming: boolean) => void;
  setStreamingAIResponse: (response: string) => void;
  setCurrentStatus: (status: string) => void;
  setConversationHistory: React.Dispatch<
    React.SetStateAction<ConversationMessage[]>
  >;
  setIsWaitingForFollowUpQueries: (waiting: boolean) => void;
  setStreamingProductGroups: React.Dispatch<
    React.SetStateAction<Map<number, string[]>>
  >;
  setProductMetadataMap: React.Dispatch<
    React.SetStateAction<Map<string, SectionBasedProduct>>
  >;
  setPreviewSearchQueries: (queries: string[]) => void;
  setPreviewQueryProducts: (
    products: Array<{
      query: string;
      queryIndex: number;
      products: any[];
      totalResults: number;
    }>
  ) => void;
  setFollowUpQuestions: (questions: string[]) => void;
  setStreamingFeaturedProduct: (
    featured: { product: SectionBasedProduct; question: string } | null
  ) => void;
  scrollToBottom: () => void;
  shouldScrollOnNextMessageRef: React.MutableRefObject<boolean>;
}

export const useStreamingSearch = ({
  brandId,
  appConfigId,
  conversationId,
  setConversationId,
  setIsActivelyStreaming,
  setStreamingAIResponse,
  setCurrentStatus,
  setConversationHistory,
  setIsWaitingForFollowUpQueries,
  setStreamingProductGroups,
  setProductMetadataMap,
  setPreviewSearchQueries,
  setPreviewQueryProducts,
  setFollowUpQuestions,
  setStreamingFeaturedProduct,
  scrollToBottom,
  shouldScrollOnNextMessageRef,
}: UseStreamingSearchProps) => {
  const performSearch = useCallback(
    async (
      searchTerm: string,
      imageData?: string,
      imageMimeType?: string,
      skipAddingUserMessage?: boolean
    ) => {
      if (!searchTerm.trim() && !imageData) {
        setStreamingAIResponse("");
        return;
      }

      // Set actively streaming flag BEFORE any state changes
      setIsActivelyStreaming(true);

      // Add user message to conversation history immediately (unless skipped)
      shouldScrollOnNextMessageRef.current = true;

      if (!skipAddingUserMessage) {
        // Check if this is a featured product query to extract the question
        const featuredProductRegex =
          /<product\s+featured="true"\s+stacklineSku="([^"]+)"(?:\s+question="([^"]+)")?\s*\/>/;
        const featuredMatch = searchTerm.match(featuredProductRegex);
        const displayContent =
          featuredMatch && featuredMatch[2] ? featuredMatch[2] : searchTerm;

        const userMessage: ConversationMessage = {
          role: "user",
          content: displayContent,
          timestamp: new Date(),
          // Include imageData for immediate display, imageUrl will come from backend on reload
          ...(imageData && {
            imageData,
            imageMimeType,
          }),
        };
        setConversationHistory((prev) => [...prev, userMessage]);
      }

      setStreamingAIResponse("");
      setIsWaitingForFollowUpQueries(false);
      setStreamingProductGroups(new Map());

      // Clear preview data immediately when new search starts to prevent old data from showing
      setPreviewSearchQueries([]);
      setPreviewQueryProducts([]);

      let fullBuffer = "";
      let currentProducts: SectionBasedProduct[] = [];
      let currentFollowUpQuestions: string[] = [];
      let featuredProductData: {
        product: SectionBasedProduct;
        question: string;
      } | null = null;
      let capturedSearchQueries: string[] = [];
      let capturedQueryProducts: Array<{
        query: string;
        queryIndex: number;
        products: any[];
        totalResults: number;
      }> = [];

      try {
        const headers = await getHeadersWithUniqueVisitorId();

        await streamApi({
          prompt: searchTerm,
          brandId: brandId || undefined,
          appConfigId: appConfigId || undefined,
          base64Image: imageData,
          imageMimeType: imageMimeType,
          // imageUrl is now optional - backend will upload if not provided
          conversationId: conversationId || undefined,
          headersToInject: headers,
          onStatusChange: (status) => {
            setCurrentStatus(status);
          },
          onConversationId: (id) => {
            setConversationId(id);
          },
          onNewProductMetadata: (products) => {
            setProductMetadataMap((prev) => {
              const newMap = new Map(prev);
              products.forEach((product) => {
                newMap.set(product.stacklineSku, product);
              });
              return newMap;
            });

            currentProducts = [...currentProducts, ...products];
            setStreamingAIResponse(processProductGroupTags(fullBuffer));
          },
          onFeaturedProduct: (product, question) => {
            // Store featured product data for later (when stream completes)
            featuredProductData = { product, question };

            // Add product to metadata map
            setProductMetadataMap((prev) => {
              const newMap = new Map(prev);
              newMap.set(product.stacklineSku, product);
              return newMap;
            });

            // IMMEDIATELY set streaming featured product to show widget below user message
            setStreamingFeaturedProduct({ product, question });

            // Scroll to show the widget
            scrollToBottom();
          },
          onEvent: (event) => {
            if (event.name === "search_products_queries") {
              capturedSearchQueries = event.data;
              setPreviewSearchQueries(event.data);
            } else if (event.name === "search_query_products") {
              // Append to our array of query products
              capturedQueryProducts = [...capturedQueryProducts, event.data];
              setPreviewQueryProducts(capturedQueryProducts);
            } else if (event.name === "follow_up_queries") {
              currentFollowUpQuestions = event.data;
              setIsWaitingForFollowUpQueries(false);
            }
          },
          onChunk: (chunk) => {
            try {
              const data = JSON.parse(chunk);
              if (data.type === "follow_up_questions" && data.content) {
                currentFollowUpQuestions = data.content;
                return;
              } else if (Array.isArray(data)) {
                return;
              }
            } catch {
              // Not JSON, treat as text
            }

            if (chunk.includes("Stream completed.")) {
              return;
            }

            fullBuffer += chunk;

            const newStreamingGroups =
              extractStreamingProductGroups(fullBuffer);
            if (newStreamingGroups.size > 0) {
              setStreamingProductGroups(newStreamingGroups);
            }

            const processedText = processProductGroupTags(fullBuffer);
            setStreamingAIResponse(processedText);
            scrollToBottom();
          },
          onComplete: () => {
            const assistantMessage: ConversationMessage = {
              role: "assistant",
              content: fullBuffer,
              timestamp: new Date(),
              products: currentProducts,
              ...(featuredProductData && {
                featuredProduct: featuredProductData,
              }),
              ...(capturedSearchQueries.length > 0 && {
                previewSearchQueries: capturedSearchQueries,
              }),
              ...(capturedQueryProducts.length > 0 && {
                previewQueryProducts: capturedQueryProducts,
              }),
            };

            setConversationHistory((prev) => [...prev, assistantMessage]);

            setFollowUpQuestions(currentFollowUpQuestions);
            setStreamingAIResponse("");
            setCurrentStatus("");
            // Clear streaming featured product (it's now in the assistant message)
            setStreamingFeaturedProduct(null);
            // Clear preview data (it's now in the assistant message)
            setPreviewSearchQueries([]);
            setPreviewQueryProducts([]);

            if (currentFollowUpQuestions.length === 0) {
              setIsWaitingForFollowUpQueries(true);
              setTimeout(() => {
                setIsWaitingForFollowUpQueries(false);
              }, 3000);
            }
          },
          onError: (error) => {
            console.error("Stream failed:", error);
            setCurrentStatus("");
          },
        });
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsActivelyStreaming(false);
      }
    },
    [
      brandId,
      appConfigId,
      conversationId,
      scrollToBottom,
      setConversationHistory,
      setConversationId,
      setCurrentStatus,
      setFollowUpQuestions,
      setIsActivelyStreaming,
      setIsWaitingForFollowUpQueries,
      setPreviewQueryProducts,
      setPreviewSearchQueries,
      setProductMetadataMap,
      setStreamingAIResponse,
      setStreamingProductGroups,
      setStreamingFeaturedProduct,
      shouldScrollOnNextMessageRef,
    ]
  );

  return { performSearch };
};
